import { useState } from "react";
import { X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import type { PendingRating } from "@/lib/storage";

interface RatingPromptProps {
  open: boolean;
  dishes: PendingRating[];
  onRate: (dishId: string, rating: "loved" | "not-for-me") => void;
  onClose: () => void;
}

const RatingPrompt = ({ open, dishes, onRate, onClose }: RatingPromptProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!open || dishes.length === 0) return null;

  const current = dishes[currentIndex];
  if (!current) return null;

  const handleRate = (rating: "loved" | "not-for-me") => {
    onRate(current.dishId, rating);
    if (currentIndex < dishes.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header accent */}
        <div className="h-1.5 bg-gradient-to-r from-primary via-red-400 to-primary" />

        <div className="px-6 pt-5 pb-2 flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {currentIndex + 1} of {dishes.length}
          </p>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="px-6 pb-8 text-center">
          <h2 className="text-2xl font-bold text-foreground mt-2 mb-1">
            How was it?
          </h2>
          <p className="text-lg font-medium text-primary mb-8">
            {current.dishName}
          </p>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => handleRate("loved")}
              className="flex flex-col items-center gap-2 p-6 rounded-2xl border-2 border-transparent bg-green-50 hover:border-green-400 hover:shadow-lg transition-all duration-200 active:scale-95"
            >
              <span className="text-5xl">😋</span>
              <span className="text-sm font-semibold text-green-700">slaps 🔥</span>
            </button>

            <button
              onClick={() => handleRate("not-for-me")}
              className="flex flex-col items-center gap-2 p-6 rounded-2xl border-2 border-transparent bg-gray-50 hover:border-gray-400 hover:shadow-lg transition-all duration-200 active:scale-95"
            >
              <span className="text-5xl">👎</span>
              <span className="text-sm font-semibold text-gray-600">mid 😐</span>
            </button>
          </div>

          <button
            onClick={() => {
              if (currentIndex < dishes.length - 1) {
                setCurrentIndex((i) => i + 1);
              } else {
                onClose();
              }
            }}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatingPrompt;
